// Typed API fetch wrapper — injects JWT Authorization header (ADR auth: access token in-memory).
// Base path proxied by Vite dev server → API. In production: same origin or CORS.
//
// Carer-parity S2: getAccessToken / setAccessToken delegate to a module-scoped
// bearer provider populated by <BearerProvider> on mount. Coordinator subtree
// registers a sessionStorage:hcm_access_token-backed provider; carer subtree
// registers a localStorage:carer_token-backed provider. apiFetch reads the
// active bearer at call time via getAccessToken — 170 consumer call sites are
// unchanged because the function signatures are preserved.

// Base URL for the main HCM API. In dev, leave VITE_API_BASE unset so the path
// stays relative ("/hcm/api/v1") and the Vite proxy forwards to localhost:3000.
// In production, set VITE_API_BASE to the API origin (e.g. https://api.hcmcrm.com)
// so the SPA at app.hcmcrm.com calls the API on its own subdomain.
const API_BASE = (import.meta.env.VITE_API_BASE ?? "") + "/hcm/api/v1";

interface BearerProviderGlue {
  getBearer: () => string | null;
  setBearer: (token: string | null) => void;
}

let _bearerProvider: BearerProviderGlue | null = null;

/** Wired by <BearerProvider> on mount; not for direct use. */
export function registerBearerProvider(p: BearerProviderGlue): void {
  _bearerProvider = p;
}

export function setAccessToken(token: string | null): void {
  _bearerProvider?.setBearer(token);
}

export function getAccessToken(): string | null {
  return _bearerProvider?.getBearer() ?? null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// P3 Cluster 2 / Ae: silent refresh on 401 via HttpOnly /auth/refresh cookie.
// Module-scoped promise serializes concurrent 401s in the same tab — multiple
// in-flight requests that all 401 share one refresh POST and one new token
// rather than racing N parallel refreshes (rotation race; one would win, the
// rest would get revoked-jti rejections).
let _refreshPromise: Promise<void> | null = null;

async function doRefresh(): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    // Cookie expired / revoked / missing — invalidate the bearer so the next
    // request doesn't keep retrying with a stale Authorization header, and let
    // downstream consumers see an unauthenticated state.
    setAccessToken(null);
    throw new Error(`Refresh failed: ${res.status}`);
  }
  const data = (await res.json()) as { access_token?: string };
  if (!data.access_token) {
    setAccessToken(null);
    throw new Error("Refresh response missing access_token");
  }
  setAccessToken(data.access_token);
}

async function refreshAccessToken(): Promise<void> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = doRefresh().finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

async function tryRefreshOnce(): Promise<boolean> {
  try { await refreshAccessToken(); return true; }
  catch { return false; }
}

async function performAuthenticatedFetch(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // credentials: include is required so the refresh-token httpOnly cookie set
  // by /auth/login on api.hcmcrm.com is sent on subsequent calls from app.hcmcrm.com.
  // Same-site (both subdomains of hcmcrm.com) so SameSite=Lax cookies still flow.
  return fetch(`${API_BASE}${path}`, { ...init, headers, credentials: "include" });
}

// Authenticated fetch with a single transparent 401→refresh→retry, throwing ApiError on non-2xx.
async function fetchOrThrow(path: string, init: RequestInit): Promise<Response> {
  let res = await performAuthenticatedFetch(path, init);
  if (res.status === 401 && await tryRefreshOnce()) {
    res = await performAuthenticatedFetch(path, init);
  }
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = null; }
    throw new ApiError(res.status, body, `API ${res.status}: ${path}`);
  }
  return res;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetchOrThrow(path, init);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Binary fetch (e.g. nsofar-rendered invoice/receipt PDF passthrough, docs/24 §4b). Carries the
// same Bearer auth + refresh-once as apiFetch, but returns the raw bytes for download/preview.
export async function apiFetchBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const res = await fetchOrThrow(path, { ...init, method: init.method ?? "GET" });
  return res.blob();
}
